import { useEffect } from 'react';

export type SeoOptions = {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'profile' | 'product' | 'event';
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  locale?: string;
};

const SITE_NAME = 'Gather';
const SITE_URL = 'https://gather-community.com';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const TAG_MARK = 'data-seo';
const JSONLD_ID = 'seo-jsonld-route';

const upsertMeta = (selector: string, attrs: Record<string, string>): void => {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(TAG_MARK, '1');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
};

const upsertLink = (rel: string, href: string, extra: Record<string, string> = {}): void => {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ''}`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    el.setAttribute(TAG_MARK, '1');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([k, v]) => el!.setAttribute(k, v));
};

const writeJsonLd = (payload: SeoOptions['jsonLd']): void => {
  const existing = document.getElementById(JSONLD_ID);
  if (existing) existing.remove();
  if (!payload) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.id = JSONLD_ID;
  script.text = JSON.stringify(payload);
  document.head.appendChild(script);
};

export const useSeo = (opts: SeoOptions): void => {
  const key = JSON.stringify(opts);
  useEffect(() => {
    const {
      title,
      description,
      canonical,
      noindex,
      ogTitle,
      ogDescription,
      ogImage,
      ogType = 'website',
      twitterCard = 'summary_large_image',
      jsonLd,
      locale,
    } = opts;

    const fullTitle = title ? `${title} — ${SITE_NAME}` : `${SITE_NAME} — Find Local Events, Clubs & Meetups Near You`;
    if (typeof document !== 'undefined') {
      document.title = fullTitle;
    }

    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
    }

    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1',
    });

    const canonicalUrl = canonical
      ? canonical.startsWith('http')
        ? canonical
        : `${SITE_URL}${canonical.startsWith('/') ? '' : '/'}${canonical}`
      : typeof window !== 'undefined'
        ? `${SITE_URL}${window.location.pathname}`
        : SITE_URL;
    upsertLink('canonical', canonicalUrl);

    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: ogType });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: ogTitle ?? fullTitle });
    if (description || ogDescription) {
      upsertMeta('meta[property="og:description"]', {
        property: 'og:description',
        content: ogDescription ?? description ?? '',
      });
    }
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage ?? DEFAULT_IMAGE });
    if (locale) {
      upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: locale });
    }

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: twitterCard });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: ogTitle ?? fullTitle });
    if (description || ogDescription) {
      upsertMeta('meta[name="twitter:description"]', {
        name: 'twitter:description',
        content: ogDescription ?? description ?? '',
      });
    }
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage ?? DEFAULT_IMAGE });

    writeJsonLd(jsonLd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

export const SEO = { SITE_NAME, SITE_URL, DEFAULT_IMAGE };
